import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireRole } from './components/RequireAuth';
import { NotFoundPage } from './pages/NotFoundPage';
import { AppShell } from './components/layout/AppShell';
import { Spinner } from './components/shared/Spinner';
import { useAuth } from './contexts/auth-context';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { HomePage } from './pages/HomePage';
import { ComingSoon } from './pages/ComingSoon';
import { RefereeClipsPage } from './pages/RefereeClipsPage';
import { RuleTestsPage } from './pages/RuleTestsPage';
import { ClipExamsPage } from './pages/ClipExamsPage';
import { ExamRunnerPage } from './pages/ExamRunnerPage';
import { ExamResultsPage } from './pages/ExamResultsPage';
import { BookmarkedPage } from './pages/BookmarkedPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { WebinarsPage } from './pages/WebinarsPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { ProfileSettings } from './pages/settings/ProfileSettings';
import { NotificationSettings } from './pages/settings/NotificationSettings';
import { SecuritySettings } from './pages/settings/SecuritySettings';
import { InvitesPage } from './pages/admin/InvitesPage';
import { FederationUsersPage } from './pages/admin/FederationUsersPage';
import { FederationsPage } from './pages/admin/FederationsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { SportsPage } from './pages/admin/SportsPage';
import { RulebooksPage } from './pages/admin/RulebooksPage';
import { SeasonsPage } from './pages/admin/SeasonsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import RefereeAnalyticsPage from './pages/admin/RefereeAnalyticsPage';
// Lazily loaded: this and PlatformCostsPage below are the only routes that pull in Recharts
// (~150kB gzipped), and both are reachable by admins alone. A static import would put a charting
// library in the initial bundle of every referee who will never open either.
const RefereeProfilePage = lazy(() => import('./pages/admin/RefereeProfilePage'));
// Lazily loaded for the same reason: Recharts, and a route only a super_admin can reach —
// statically importing it would put a charting library in the initial bundle of every referee.
const PlatformCostsPage = lazy(() => import('./pages/admin/PlatformCostsPage'));
import { ClipsPage } from './pages/admin/ClipsPage';
import { SuperAdminClipsPage } from './pages/admin/SuperAdminClipsPage';
import { ClipReviewPage } from './pages/admin/ClipReviewPage';
import { ClipEditorPage } from './pages/admin/ClipEditorPage';
import { DailyQueuePage } from './pages/admin/DailyQueuePage';
import { ExamsPage } from './pages/admin/ExamsPage';
import { ExamEditorPage } from './pages/admin/ExamEditorPage';
import { ExamResultDetailPage } from './pages/admin/ExamResultDetailPage';
import { ExamResultsAdminPage } from './pages/admin/ExamResultsAdminPage';

// The app index is role-shaped: a super_admin has no referee Home, so it lands on the console
// instead of the daily-loop Home that referees/federation_admins see.
function RoleHome() {
  const { role } = useAuth();
  if (role === 'super_admin') return <Navigate to="/federations" replace />;
  return <HomePage />;
}

// /admin/clips is shared by both admin roles but the experience differs: super_admin gets the
// cross-tenant sport-picker console; a federation_admin gets their single-federation management
// table. One route, role-dispatched, so the sidebar link stays the same for both.
function AdminClipsRoute() {
  const { role } = useAuth();
  return role === 'super_admin' ? <SuperAdminClipsPage /> : <ClipsPage />;
}


// The read-only settings tabs take the bits of the session they need as PROPS rather than reading
// the auth context themselves — it keeps each a pure function of its inputs, and the role-filtering
// in NotificationSettings trivial to test. ProfileSettings is the exception and reads the context
// directly, because it also WRITES to it (setProfile) after a rename; threading a context setter
// down as a prop would be ceremony, not decoupling.
function SettingsNotificationsRoute() {
  const { role } = useAuth();
  return <NotificationSettings role={role} />;
}

function SettingsSecurityRoute() {
  const { user } = useAuth();
  return <SecuritySettings email={user?.email ?? null} />;
}

// Public auth routes + a protected app shell. Referee nav destinations render a placeholder
// this slice; the admin invite module is additionally gated by role.
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<RoleHome />} />
          {/* Referee daily-loop / browse view of clips — separate from the admin module
              at /admin/clips so the IA stays clean (one place for managing, another for
              browsing). The library shell + daily clip + decision options render now;
              answering + reveal lands with M6 (attempts write path). */}
          <Route path="clips" element={<RefereeClipsPage />} />
          {/* Exam taking (M10 · 10e): the two landings + the shared runner/results routes. */}
          <Route path="rule-tests" element={<RuleTestsPage />} />
          <Route path="exams" element={<ClipExamsPage />} />
          <Route path="exam-attempts/:id/run" element={<ExamRunnerPage />} />
          <Route path="exam-attempts/:id/results" element={<ExamResultsPage />} />
          <Route path="bookmarked" element={<BookmarkedPage />} />
          {/* M11 — documents library, webinars, and the leaderboard UI. */}
          <Route path="documents" element={<DocumentsPage />} />
          {/* Separate routes (not tab state) for the same reason the exam/analytics tabs are
              routed: an admin can send a colleague a link straight to "past webinars". */}
          <Route path="webinars" element={<WebinarsPage when="upcoming" />} />
          <Route path="webinars/past" element={<WebinarsPage when="past" />} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          {/* M14 — the bell's full history. Not role-gated: every role has an inbox, and the
              API scopes every read to the caller. */}
          <Route path="notifications" element={<NotificationsPage />} />

          {/* M14 — the account area. Nested routes rather than tab state, like every other tab
              strip here: the topbar user menu can point straight at Security, and a deep link
              opens on the right tab. Not role-gated — everyone has a name and a password. */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<ProfileSettings />} />
            <Route path="notifications" element={<SettingsNotificationsRoute />} />
            <Route path="security" element={<SettingsSecurityRoute />} />
          </Route>

          {/* super_admin console: provision tenants + manage the global sport/rule-book catalogue. */}
          <Route element={<RequireRole roles={['super_admin']} />}>
            {/* Platform cost board — super_admin only. Never federation_admin: what each tenant
                costs, and the rate card behind it, is cross-tenant commercial data. */}
            <Route
              path="admin/costs"
              element={
                <Suspense fallback={<div className="p-8"><Spinner /></div>}>
                  <PlatformCostsPage />
                </Suspense>
              }
            />
            <Route path="federations" element={<FederationsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="sports" element={<SportsPage />} />
            <Route path="rulebooks" element={<RulebooksPage />} />
            {/* The Leaderboard's Period="Season" calendar — platform-wide, super_admin managed. */}
            <Route path="seasons" element={<SeasonsPage />} />
          </Route>

          <Route element={<RequireRole roles={['federation_admin', 'super_admin']} />}>
            {/* Federation analytics dashboard (M12). Federation-scoped; a super_admin lands on the
                zeroed board with a "platform view coming soon" note (see AnalyticsPage). */}
            <Route path="admin/analytics" element={<AnalyticsPage />} />
            {/* The Referees tab and its drill-down. Separate routes rather than tab state so an
                admin can link a colleague straight to one official (same reasoning as ExamTabs).
                Both inherit this block's federation_admin | super_admin guard; the API additionally
                re-checks that the referee belongs to the caller's federation. */}
            <Route path="admin/analytics/referees" element={<RefereeAnalyticsPage />} />
            <Route
              path="admin/analytics/referees/:userId"
              element={
                <Suspense fallback={<div className="p-8"><Spinner /></div>}>
                  <RefereeProfilePage />
                </Suspense>
              }
            />
            <Route path="invites" element={<InvitesPage />} />
            {/* The Users page's second tab: the federation's existing roster, with
                removal. Separate route (not tab state) so it can be linked directly, same
                reasoning as every other RouteTabs pairing in this app. */}
            <Route path="invites/users" element={<FederationUsersPage />} />
            {/* Admin clip-management module — list + upload modal + editor. Lives under
                /admin/clips so it stays distinct from the referee-facing /clips entry.
                The list is role-dispatched (super_admin console vs federation_admin table). */}
            <Route path="admin/clips" element={<AdminClipsRoute />} />
            <Route path="admin/clips/:id/review" element={<ClipReviewPage />} />
            <Route path="admin/clips/:id/edit" element={<ClipEditorPage />} />
            {/* The federation+sport's daily-clip rotation: upcoming-days preview + reorderable
                queue. super_admin picks a sport + federation first; federation_admin's own scope
                is implicit (see DailyQueuePage). */}
            <Route path="admin/daily-queue" element={<DailyQueuePage />} />
            {/* Exam authoring (M10 · 10d) — list + editor. Both roles author; the service
                scopes a federation_admin to their own federation. */}
            {/* Rule tests / Video tests — two tabs sharing ExamsPage (RouteTabs-linked below),
                same "one component, a type prop, two routes" shape as WebinarsPage's when. */}
            <Route path="admin/exams" element={<ExamsPage examType="rule_test" />} />
            <Route path="admin/exams/video-tests" element={<ExamsPage examType="video_clip_exam" />} />
            <Route path="admin/exams/:id/edit" element={<ExamEditorPage />} />
            <Route path="admin/exams/:id/results" element={<ExamResultsAdminPage />} />
            <Route path="admin/exams/:id/results/:attemptId" element={<ExamResultDetailPage />} />
          </Route>

          {/* Catchall for authed users — renders inside the AppShell so the sidebar + topbar
              stay visible. Unauthed visitors hitting a bad URL fall through to RequireAuth's
              redirect to /login (consistent with every other protected route). */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
